import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ShieldCheck, ShieldAlert, Shield, AlertTriangle, X } from 'lucide-react';
import { Skill, SecurityReview, SecurityDimension, SKILL_RISK_COLOR } from './types';
import { SkillIcon } from './iconMap';
type Phase = 'fetching' | 'security' | 'contract' | 'installed';
interface Props {
  skill: Skill | null;
  onClose: () => void;
  onInstalled: (id: string) => void;
}
const DEFAULT_REVIEW: SecurityReview = {
  verdict: 'clean',
  confidence: 'high',
  model: 'claude-haiku-4-5',
  dimensions: [{
    key: 'purpose_matches_claims',
    label: 'Purpose matches claims',
    rating: 'ok'
  }, {
    key: 'instruction_scope',
    label: 'Instruction scope',
    rating: 'ok'
  }, {
    key: 'install_mechanism_safe',
    label: 'Install mechanism safe',
    rating: 'ok'
  }, {
    key: 'credential_proportionality',
    label: 'Credential proportionality',
    rating: 'ok'
  }, {
    key: 'zenbot_env_exposure',
    label: 'ZenBot env exposure',
    rating: 'ok'
  }]
};
export function InstallSheet({
  skill,
  onClose,
  onInstalled
}: Props) {
  const [phase, setPhase] = useState<Phase>('fetching');
  useEffect(() => {
    if (!skill) return;
    setPhase('fetching');
    const t = setTimeout(() => setPhase('security'), 1100);
    return () => clearTimeout(t);
  }, [skill?.id]);
  useEffect(() => {
    if (!skill) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [skill, onClose]);
  if (!skill) return null;
  const review = skill.securityReview ?? DEFAULT_REVIEW;
  return <AnimatePresence data-id="element-1719">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 bg-black/60 z-[70]" onClick={onClose} data-id="element-1720" />
      <motion.div initial={{
      y: '100%'
    }} animate={{
      y: 0
    }} exit={{
      y: '100%'
    }} transition={{
      type: 'spring',
      damping: 32,
      stiffness: 280
    }} drag="y" dragConstraints={{
      top: 0,
      bottom: 0
    }} dragElastic={0.2} onDragEnd={(_, info) => {
      if (info.offset.y > 120) onClose();
    }} className="fixed inset-x-0 bottom-0 z-[71] bg-[#141418] rounded-t-[20px] border-t border-white/[0.06] flex flex-col" style={{
      height: '90vh'
    }} data-id="element-1721">
        <div className="flex justify-center pt-2 pb-1 shrink-0" data-id="element-1722">
          <div className="w-10 h-1 rounded-full bg-[#8a8680]/30" data-id="element-1723" />
        </div>

        <div className="px-5 py-4 border-b border-white/[0.04] flex items-center gap-3 shrink-0" data-id="element-1724">
          <div className="w-10 h-10 rounded-[12px] bg-[#252530] flex items-center justify-center" data-id="element-1725">
            <SkillIcon icon={skill.icon} className="text-[#e8e4df]" data-id="element-1726" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-1727">
            <div className="text-[14px] text-[#e8e4df]" data-id="element-1728">{skill.name}</div>
            <div className="text-[11px] text-[#8a8680]" data-id="element-1729">
              {skill.author} · v{skill.version}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/[0.04] text-[#8a8680]" data-id="element-1730">
            <X size={14} data-id="element-1731" />
          </button>
        </div>

        <PhaseIndicator phase={phase} data-id="element-1732" />

        <div className="flex-1 overflow-y-auto" data-id="element-1733">
          <AnimatePresence mode="wait" data-id="element-1734">
            {phase === 'fetching' && <motion.div key="fetching" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="flex flex-col items-center justify-center py-20" data-id="element-1735">
                <div className="w-6 h-6 border-2 border-[#4ac0d4]/30 border-t-[#4ac0d4] rounded-full animate-spin mb-4" data-id="element-1736" />
                <div className="text-[13px] text-[#e8e4df]" data-id="element-1737">Downloading bundle from clawhub…</div>
                <div className="text-[11px] text-[#8a8680] mt-1 font-mono" data-id="element-1738">
                  {skill.sha256.slice(0, 16)}…
                </div>
              </motion.div>}

            {phase === 'security' && <motion.div key="security" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="px-5 py-5" data-id="element-1739">
                <SecurityReviewPhase review={review} onProceed={() => setPhase('contract')} onRefine={() => setPhase('contract')} onReject={onClose} data-id="element-1740" />
              </motion.div>}

            {phase === 'contract' && <motion.div key="contract" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="px-5 py-5" data-id="element-1741">
                <ContractReviewPhase skill={skill} onApprove={() => {
              setPhase('installed');
              setTimeout(() => onInstalled(skill.id), 900);
            }} onRefine={() => {}} onReject={onClose} data-id="element-1742" />
              </motion.div>}

            {phase === 'installed' && <motion.div key="installed" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="flex flex-col items-center justify-center py-20" data-id="element-1743">
                <div className="w-14 h-14 rounded-full bg-[#4ac0d4]/15 border border-[#4ac0d4]/30 flex items-center justify-center mb-4" data-id="element-1744">
                  <ShieldCheck size={24} className="text-[#4ac0d4]" strokeWidth={1.8} data-id="element-1745" />
                </div>
                <div className="text-[15px] text-[#e8e4df]" data-id="element-1746">Installed</div>
                <div className="text-[12px] text-[#8a8680] mt-1" data-id="element-1747">
                  {skill.name} is available for approved calls
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>;
}
function PhaseIndicator({
  phase
}: {
  phase: Phase;
}) {
  const steps: {
    id: Phase;
    label: string;
  }[] = [{
    id: 'fetching',
    label: 'Fetch'
  }, {
    id: 'security',
    label: 'Security'
  }, {
    id: 'contract',
    label: 'Contract'
  }, {
    id: 'installed',
    label: 'Done'
  }];
  const currentIdx = steps.findIndex(s => s.id === phase);
  return <div className="px-5 py-3 border-b border-white/[0.04] flex items-center gap-2 shrink-0" data-id="element-1748">
      {steps.map((step, i) => {
      const done = i < currentIdx;
      const active = i === currentIdx;
      return <React.Fragment key={step.id} data-id="element-1749">
            <div className="flex items-center gap-1.5" data-id="element-1750">
              <div className="w-4 h-4 rounded-full flex items-center justify-center text-[9px]" style={{
            background: done || active ? '#4ac0d4' : 'transparent',
            border: done || active ? 'none' : '1px solid #8a868044',
            color: '#141418'
          }} data-id="element-1751">
                {done && <Check size={9} strokeWidth={3} data-id="element-1752" />}
                {active && <div className="w-1.5 h-1.5 rounded-full bg-[#141418]" data-id="element-1753" />}
              </div>
              <span className="text-[11px]" style={{
            color: done || active ? '#e8e4df' : '#8a8680'
          }} data-id="element-1754">
                {step.label}
              </span>
            </div>
            {i < steps.length - 1 && <div className="flex-1 h-px" style={{
          background: done ? '#4ac0d4' : '#8a868022'
        }} data-id="element-1755" />}
          </React.Fragment>;
    })}
    </div>;
}
function SecurityReviewPhase({
  review,
  onProceed,
  onRefine,
  onReject
}: {
  review: SecurityReview;
  onProceed: () => void;
  onRefine: () => void;
  onReject: () => void;
}) {
  const verdictColor = review.verdict === 'clean' ? '#4ac0d4' : review.verdict === 'suspicious' ? '#e8a84c' : '#e86c4c';
  const VerdictIcon = review.verdict === 'clean' ? ShieldCheck : review.verdict === 'suspicious' ? ShieldAlert : AlertTriangle;
  return <div data-id="element-1756">
      <div className="flex items-center gap-3 mb-5" data-id="element-1757">
        <VerdictIcon size={18} style={{
        color: verdictColor
      }} strokeWidth={1.8} data-id="element-1758" />
        <div data-id="element-1759">
          <div className="text-[14px] text-[#e8e4df] capitalize" data-id="element-1760">{review.verdict}</div>
          <div className="text-[11px] text-[#8a8680]" data-id="element-1761">
            {review.confidence} confidence · {review.model}
          </div>
        </div>
      </div>

      <div className="space-y-2 mb-6" data-id="element-1762">
        {review.dimensions.map(dim => <DimensionRow key={dim.key} dim={dim} data-id="element-1763" />)}
      </div>

      {review.verdict === 'clean' ? <button onClick={onProceed} className="w-full h-11 rounded-full bg-[#4ac0d4]/15 border border-[#4ac0d4]/30 text-[#4ac0d4] text-[13px] hover:bg-[#4ac0d4]/25 transition-colors" data-id="element-1764">
          Continue to contract review
        </button> : <div className="space-y-2" data-id="element-1765">
          <button onClick={onRefine} className="w-full h-11 rounded-full bg-[#e8a84c]/15 border border-[#e8a84c]/30 text-[#e8a84c] text-[13px] hover:bg-[#e8a84c]/25 transition-colors" data-id="element-1766">
            Refine review with feedback
          </button>
          <div className="flex gap-2" data-id="element-1767">
            <button onClick={onProceed} className="flex-1 h-10 rounded-full border border-white/[0.06] text-[#8a8680] text-[12px] hover:text-[#e8e4df] hover:bg-white/[0.03] transition-colors" data-id="element-1768">
              Proceed anyway
            </button>
            <button onClick={onReject} className="flex-1 h-10 rounded-full border border-white/[0.06] text-[#8a8680] text-[12px] hover:text-[#e8e4df] hover:bg-white/[0.03] transition-colors" data-id="element-1769">
              Reject
            </button>
          </div>
        </div>}
    </div>;
}
function DimensionRow({
  dim
}: {
  dim: SecurityDimension;
}) {
  const color = dim.rating === 'ok' ? '#4ac0d4' : dim.rating === 'note' ? '#8a8680' : '#e8a84c';
  const Icon = dim.rating === 'warn' ? ShieldAlert : dim.rating === 'note' ? Shield : ShieldCheck;
  return <div className="bg-[#1c1c22] rounded-[12px] px-4 py-3 border" style={{
    borderColor: `${color}22`
  }} data-id="element-1770">
      <div className="flex items-center gap-2" data-id="element-1771">
        <Icon size={12} style={{
        color
      }} strokeWidth={1.8} data-id="element-1772" />
        <span className="text-[12px] text-[#e8e4df]" data-id="element-1773">{dim.label}</span>
        <span className="text-[10px] uppercase tracking-wide ml-auto" style={{
        color
      }} data-id="element-1774">
          {dim.rating}
        </span>
      </div>
      {dim.note && <div className="text-[11px] text-[#8a8680] mt-1 ml-5" data-id="element-1775">{dim.note}</div>}
    </div>;
}
function ContractReviewPhase({
  skill,
  onApprove,
  onRefine,
  onReject
}: {
  skill: Skill;
  onApprove: () => void;
  onRefine: () => void;
  onReject: () => void;
}) {
  const [showYaml, setShowYaml] = useState(false);
  const riskColor = SKILL_RISK_COLOR[skill.risk];
  const yaml = `contract_version: 1
skill:
  id: ${skill.slug}
  version: ${skill.version}
  source: { registry: clawhub, slug: ${skill.slug}, sha256: ${skill.sha256.slice(0, 16)}… }
  risk_level: ${skill.risk}
  execution: manual  # every invocation requires human confirmation
tools:
${skill.tools.map(t => `  - name: ${t.name}
    summary: ${JSON.stringify(t.summary)}
    policy:
      human_approval_required: true
      side_effects: ${t.sideEffects.writes || t.sideEffects.network ? 'true' : 'false'}`).join('\n')}
`;
  return <div data-id="element-1776">
      <div className="flex items-center gap-3 mb-5 flex-wrap" data-id="element-1777">
        <div className="inline-flex items-center justify-center h-[22px] px-2 rounded-full text-[10px] uppercase tracking-wide" style={{
        background: `${riskColor}15`,
        border: `1px solid ${riskColor}33`,
        color: riskColor
      }} data-id="element-1778">
          {skill.risk} risk
        </div>
        <span className="text-[11px] text-[#8a8680]" data-id="element-1779">Every tool call requires your confirmation</span>
      </div>

      <div className="space-y-2 mb-5" data-id="element-1780">
        {skill.tools.map(tool => <div key={tool.name} className="bg-[#1c1c22] rounded-[12px] p-4 border border-white/[0.04]" data-id="element-1781">
            <div className="flex items-center gap-2 mb-1" data-id="element-1782">
              <span className="text-[13px] text-[#e8e4df] font-mono" data-id="element-1783">{tool.name}</span>
              <span className="text-[9px] uppercase tracking-wide text-[#e8a84c] border border-[#e8a84c]/40 rounded-full px-1.5 py-0.5" data-id="element-1784">
                Approval
              </span>
            </div>
            <div className="text-[12px] text-[#8a8680] mb-2" data-id="element-1785">{tool.summary}</div>
            <div className="text-[10px] text-[#8a8680]/70 font-mono" data-id="element-1786">{tool.inputSchemaPreview}</div>
            <div className="flex items-center gap-2 mt-2 flex-wrap" data-id="element-1787">
              {tool.sideEffects.reads && <SideEffectChip label="reads" data-id="element-1788" />}
              {tool.sideEffects.writes && <SideEffectChip label="writes" data-id="element-1789" />}
              {tool.sideEffects.network && <SideEffectChip label="network" data-id="element-1790" />}
              {tool.sideEffects.userFacing && <SideEffectChip label="user-facing" data-id="element-1791" />}
            </div>
          </div>)}
      </div>

      <button onClick={() => setShowYaml(!showYaml)} className="text-[11px] text-[#8a8680] hover:text-[#e8e4df] mb-3 transition-colors" data-id="element-1792">
        {showYaml ? 'Hide' : 'Show'} contract YAML
      </button>
      <AnimatePresence data-id="element-1793">
        {showYaml && <motion.pre initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="text-[11px] text-[#8a8680] font-mono bg-black/30 rounded-[12px] px-4 py-3 mb-5 overflow-x-auto whitespace-pre-wrap" data-id="element-1794">
            {yaml}
          </motion.pre>}
      </AnimatePresence>

      <div className="space-y-2" data-id="element-1795">
        <button onClick={onApprove} className="w-full h-11 rounded-full bg-[#4ac0d4]/15 border border-[#4ac0d4]/30 text-[#4ac0d4] text-[13px] hover:bg-[#4ac0d4]/25 transition-colors" data-id="element-1796">
          Approve and install
        </button>
        <div className="flex gap-2" data-id="element-1797">
          <button onClick={onRefine} className="flex-1 h-10 rounded-full border border-white/[0.06] text-[#8a8680] text-[12px] hover:text-[#e8e4df] hover:bg-white/[0.03] transition-colors" data-id="element-1798">
            Refine
          </button>
          <button onClick={onReject} className="flex-1 h-10 rounded-full border border-white/[0.06] text-[#8a8680] text-[12px] hover:text-[#e8e4df] hover:bg-white/[0.03] transition-colors" data-id="element-1799">
            Reject
          </button>
        </div>
      </div>
    </div>;
}
function SideEffectChip({
  label
}: {
  label: string;
}) {
  return <span className="text-[10px] text-[#8a8680] bg-white/[0.03] px-2 py-0.5 rounded-full" data-id="element-1800">
      {label}
    </span>;
}